import {
    buildSocialRegistrationChallengeUrl,
    createSocialRegistrationChallenge,
    getSocialRegistrationIssuer,
    SocialRegistrationProvider,
    SocialRegistrationOutcome,
    SocialRegistrationResumeParams,
    ExternalRegistrationPeekResponse,
} from 'common/api/auth/socialRegistration';
import { RegistrationRequestWithoutOrg } from 'common/api/auth/models/RegistrationRequestWithoutOrg';
import { Organization } from 'common/api/crm/models/Organization';
import { Routes } from 'routes';

export { createSocialRegistrationChallenge };

const socialRegistrationStateStorageKey = 'org-enrollment-v2-social-registration';

interface PersistedSocialRegistrationState {
    organization: Organization;
    userInformation: RegistrationRequestWithoutOrg;
    currentStep: number;
    maxStep: number;
    codeVerifier: string;
}

const socialRegistrationErrorMessages: Partial<Record<SocialRegistrationOutcome, string>> = {
    invalid_key: 'We could not finish sign-in. Please try again.',
    expired_key: 'Your sign-in session expired. Please start sign-in again.',
    used_key: 'That sign-in link has already been used. Please start sign-in again.',
};

export const getEnrollmentV2SocialRegistrationIssuer = () => getSocialRegistrationIssuer();

export const buildEnrollmentV2SocialRegistrationReturnUrl = () =>
    new URL(Routes.EnrollV2, window.location.origin).toString();

export const buildEnrollmentV2SocialRegistrationChallengeUrl = (
    provider: SocialRegistrationProvider,
    challenge: ReturnType<typeof createSocialRegistrationChallenge>,
    scheme?: string
) =>
    buildSocialRegistrationChallengeUrl(
        provider,
        buildEnrollmentV2SocialRegistrationReturnUrl(),
        getEnrollmentV2SocialRegistrationIssuer(),
        challenge,
        scheme
    );

export const exchangeEnrollmentV2SocialRegistrationSession = (
    externalRegistrationKey: string,
    codeVerifier: string
): Promise<ExternalRegistrationPeekResponse> =>
    fetch(`/api/local/social-registration/session`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF': '1',
        },
        body: JSON.stringify({
            externalRegistrationKey,
            codeVerifier,
        }),
    }).then(async (response) => {
        if (!response.ok) {
            throw new Error('Unable to peek external registration session.');
        }

        return response.json();
    });

export const getSocialRegistrationResumeParams = (
    search: string
): SocialRegistrationResumeParams => {
    const params = new URLSearchParams(search);

    return {
        outcome: params.get('outcome') || undefined,
        provider: params.get('provider') || undefined,
        externalRegistrationKey: params.get('externalRegistrationKey') || undefined,
    };
};

export const persistEnrollmentV2SocialRegistrationState = (
    state: PersistedSocialRegistrationState
) => {
    sessionStorage.setItem(socialRegistrationStateStorageKey, JSON.stringify(state));
};

export const restoreEnrollmentV2SocialRegistrationState = () => {
    const rawState = sessionStorage.getItem(socialRegistrationStateStorageKey);

    if (!rawState) {
        return null;
    }

    try {
        return JSON.parse(rawState) as PersistedSocialRegistrationState;
    } catch (_error) {
        clearEnrollmentV2SocialRegistrationState();
        return null;
    }
};

export const clearEnrollmentV2SocialRegistrationState = () => {
    sessionStorage.removeItem(socialRegistrationStateStorageKey);
};

export const getSocialRegistrationErrorMessage = (outcome?: string) => {
    if (!outcome) {
        return 'We could not finish sign-in. Please try again.';
    }

    return (
        socialRegistrationErrorMessages[outcome as SocialRegistrationOutcome] ||
        'We could not finish sign-in. Please try again.'
    );
};
